import {
  Children,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Collapsible } from 'radix-ui';

import { TimelineConnector } from '@components/patterns/Timeline/connector';
import {
  TimelineContext,
  TimelineEventContext,
  TimelineEventVariant,
} from '@components/patterns/Timeline/context';
import { TimelineContent } from '@components/patterns/Timeline/TimelineContent';
import { TimelineIcon } from '@components/patterns/Timeline/TimelineIcon';
import { TimelineTimestamp } from '@components/patterns/Timeline/TimelineTimestamp';
import { TimelineTitle } from '@components/patterns/Timeline/TimelineTitle';
import { TimestampFormat } from '@components/primitives/Timestamp';
import { easeOutSmooth } from '@components/primitives/utilities';

import pluralize from '@utils/pluralize';

import css from './styles.module.css';

export interface TimelineSubTimelineProps extends PropsWithChildren {
  title?: ReactNode;
  defaultOpen?: boolean;
}

function TimelineSubTimeline({
  children,
  title,
  defaultOpen,
}: TimelineSubTimelineProps) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  const count = Children.toArray(children).filter(Boolean).length;

  return (
    <Collapsible.Root asChild open={open} onOpenChange={setOpen}>
      <li className={css.subTimelineRoot}>
        <div className={css.subTimelineHeader}>
          <Collapsible.Trigger className={css.subTimelineTrigger}>
            {title ? (
              <>
                <span>{title}</span>
                {count >= 2 && (
                  <span className={css.subTimelineCount}>
                    {pluralize(count, 'event')}
                  </span>
                )}
              </>
            ) : (
              <span>{pluralize(count, 'more event')}</span>
            )}

            <span className={css.subTimelineTriggerChevron} aria-hidden>
              <ChevronRight aria-hidden />
            </span>
          </Collapsible.Trigger>
        </div>

        {/* Always mounted so the trigger's aria-controls target exists. */}
        <Collapsible.Content forceMount>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                className={css.subTimelinePanel}
                initial={{ opacity: 1, height: 14 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 14 }}
                transition={{ duration: 0.25, ease: easeOutSmooth }}
              >
                <div
                  className={css.subTimelineConnector}
                  data-position="head"
                />
                <ol className={css.subTimeline}>{children}</ol>
                <div
                  className={css.subTimelineConnector}
                  data-position="tail"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Collapsible.Content>
      </li>
    </Collapsible.Root>
  );
}

export interface TimelineEventProps extends PropsWithChildren {
  variant?: TimelineEventVariant;
}

function TimelineEvent({ children, variant = 'info' }: TimelineEventProps) {
  const context = useMemo(() => ({ variant }), [variant]);

  return (
    <TimelineEventContext.Provider value={context}>
      <li className={css.event} data-variant={variant}>
        {children}
      </li>
    </TimelineEventContext.Provider>
  );
}

export interface TimelineRootProps extends PropsWithChildren {
  /**
   * The timestamp format to use for all events in the timeline.
   *
   * If not provided, the timestamp will be formatted relative to the current time.
   */
  timestampFormat?: TimestampFormat;
}

function TimelineRoot({ children, timestampFormat }: TimelineRootProps) {
  const context = useMemo(() => ({ timestampFormat }), [timestampFormat]);

  return (
    <TimelineContext.Provider value={context}>
      <div className={css.timelineRoot}>
        <TimelineConnector />
        <ol className={css.timeline}>{children}</ol>
      </div>
    </TimelineContext.Provider>
  );
}

export const Timeline = Object.assign(TimelineRoot, {
  Event: TimelineEvent,
  SubTimeline: TimelineSubTimeline,
  Icon: TimelineIcon,
  Title: TimelineTitle,
  Timestamp: TimelineTimestamp,
  Content: TimelineContent,
});

export type { TimelineEventVariant } from './context';
export type { TimelineContentProps } from './TimelineContent';
export type { TimelineIconProps } from './TimelineIcon';
export type { TimelineTimestampProps } from './TimelineTimestamp';
export type { TimelineTitleProps } from './TimelineTitle';
